import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { offersService, type Offer } from '../../services/offers.service';
import { formatSAR } from '../../utils/formatters';
import api from '../../services/api';
import {
  ShieldCheck, Globe, Package, Receipt, Building2, UserCheck, FileText,
  MapPin, Clock, CheckCircle, CheckCircle2, XCircle, Phone, Mail,
  ChevronDown, ArrowRight, Check, PhoneCall, HardHat, Wifi,
  Link2Off, AlertCircle, Info, Loader2, Send, Radio,
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

type ViewStage =
  | 'loading'
  | 'landing'
  | 'otp'
  | 'accepted'
  | 'rejected'
  | 'expired'
  | 'already-actioned'
  | 'invalid';

type OtpAction = 'accept' | 'reject';

// ─── Category config ─────────────────────────────────────────────────────────

const catConfig: Record<string, { bg: string; color: string; Icon: React.FC<any> }> = {
  '5G':       { bg: '#EEF2FF', color: '#4F46E5', Icon: Wifi },
  Fixed_BBI:  { bg: '#E0F7FA', color: '#0097A7', Icon: Radio },
  Fixed_BDI:  { bg: '#FFF3E0', color: '#E65100', Icon: Radio },
  Mobility:   { bg: '#F3E8FF', color: '#7E22CE', Icon: Send },
};
const catLabels: Record<string, string> = {
  '5G': '5G Fixed', Fixed_BBI: 'Fixed BBI', Fixed_BDI: 'Fixed BDI', Mobility: 'Mobility',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionTitle: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div className="offer-section-title">{icon}{children}</div>
);

const InfoGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>
);

const InfoCell: React.FC<{ label: string; value?: string | null; rtl?: boolean }> = ({ label, value, rtl }) => (
  <div>
    <div style={{ fontSize: 11.5, color: 'var(--text-3)', fontWeight: 600, marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13.5, fontWeight: 500 }} dir={rtl ? 'rtl' : undefined}>{value || '—'}</div>
  </div>
);

const EdgeCard: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  body: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ icon, iconBg, title, body, footer }) => (
  <div className="co-edge-wrap" style={{ width: '100%' }}>
    <div className="co-edge-icon" style={{ background: iconBg }}>{icon}</div>
    <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)', marginBottom: 10 }}>{title}</h2>
    {body}
    {footer}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const CustomerOfferView: React.FC = () => {
  const { offerId } = useParams<{ offerId: string }>();

  const [offer, setOffer] = useState<Offer | null>(null);
  const [stage, setStage] = useState<ViewStage>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  // OTP state
  const [otpAction, setOtpAction] = useState<OtpAction>('accept');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [maskedContact, setMaskedContact] = useState('');
  const [_otpExpiry, setOtpExpiry] = useState<Date | null>(null);
  const [otpError, setOtpError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // UI state
  const [tcsOpen, setTcsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOther, setRejectOther] = useState('');
  const [rejectionSubmitted, setRejectionSubmitted] = useState(false);

  // Load offer
  useEffect(() => {
    if (!offerId) { setStage('invalid'); setErrorMsg('No offer ID in URL.'); return; }
    offersService.getPublic(offerId)
      .then((o) => { setOffer(o); setStage('landing'); })
      .catch((err: { response?: { data?: { message?: string }; status?: number } }) => {
        const msg = (err.response?.data?.message ?? '').toLowerCase();
        if (msg.includes('expired') || msg.includes('closed')) setStage('expired');
        else if (msg.includes('no longer available') || msg.includes('not available')) setStage('already-actioned');
        else { setStage('invalid'); setErrorMsg(err.response?.data?.message ?? 'This offer link is invalid.'); }
      });
  }, [offerId]);

  // OTP countdown
  const startCountdown = (secs = 30) => {
    if (resendTimer.current) clearInterval(resendTimer.current);
    setResendCountdown(secs);
    resendTimer.current = setInterval(() => {
      setResendCountdown((c) => {
        if (c <= 1) { clearInterval(resendTimer.current!); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (resendTimer.current) clearInterval(resendTimer.current); }, []);

  const sendOtp = async (action: OtpAction) => {
    if (!offer) return;
    setOtpAction(action);
    setOtpSending(true);
    setOtpError('');
    setOtpDigits(['', '', '', '', '', '']);
    try {
      const res = await api.post<{ expiresAt: string; maskedContact: string; channel: string }>(
        `/otp/${offer.id}/generate?action=${action}`, {},
      );
      setMaskedContact(res.data.maskedContact ?? '');
      setOtpExpiry(new Date(res.data.expiresAt));
      startCountdown(30);
      setStage('otp');
      setTimeout(() => otpRefs.current[0]?.focus(), 150);
    } catch (err: any) {
      setOtpError(err.response?.data?.message ?? 'Failed to send OTP.');
    } finally { setOtpSending(false); }
  };

  const handleOtpChange = (idx: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[idx] = value;
    setOtpDigits(next);
    if (value && idx < 5) otpRefs.current[idx + 1]?.focus();
    if (value && idx === 5) {
      // auto-submit when last digit filled
      const full = [...next].join('');
      if (full.length === 6) verifyOtp([...next]);
    }
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const verifyOtp = async (digits?: string[]) => {
    const code = (digits ?? otpDigits).join('');
    if (code.length < 6) { setOtpError('Please enter the full 6-digit code.'); return; }
    if (!offer) return;
    setOtpVerifying(true);
    setOtpError('');
    try {
      await api.post(`/otp/${offer.id}/verify?action=${otpAction}`, { otp: code });
      setStage(otpAction === 'accept' ? 'accepted' : 'rejected');
    } catch (err: any) {
      setOtpError(err.response?.data?.message ?? 'Verification failed. Please try again.');
      setOtpDigits(['', '', '', '', '', '']);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } finally { setOtpVerifying(false); }
  };

  const resendOtp = () => sendOtp(otpAction);

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const Header = () => (
    <header className="co-header">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, background: 'var(--p)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15, color: '#fff' }}>S</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.1 }}>Salam Telecom</div>
          <div style={{ fontSize: 10.5, color: 'var(--text-3)', letterSpacing: '.04em' }}>CONNECTIVITY OFFER</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 5 }}>
          <ShieldCheck size={13} color="var(--p)" /> Secure &amp; Verified
        </div>
        <button style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 20, border: '1.5px solid var(--border)', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', cursor: 'pointer', background: '#fff', fontFamily: 'inherit' }}>
          <Globe size={13} /> EN
        </button>
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="co-footer">
      <a href="#" style={{ fontSize: 12, color: 'var(--text-3)', textDecoration: 'none' }}>Terms &amp; Conditions</a>
      <span style={{ color: 'var(--text-3)' }}>·</span>
      <a href="#" style={{ fontSize: 12, color: 'var(--text-3)', textDecoration: 'none' }}>Privacy Policy</a>
      <span style={{ color: 'var(--text-3)' }}>·</span>
      <a href="tel:920000123" style={{ fontSize: 12, color: 'var(--text-3)', textDecoration: 'none' }}>Support: 920 000 123</a>
      <span style={{ color: 'var(--text-3)' }}>·</span>
      <span style={{ fontSize: 11.5, background: '#F3F4F6', color: 'var(--text-2)', padding: '2px 9px', borderRadius: 20, fontWeight: 600 }}>Classification: Public</span>
    </footer>
  );

  const SupportSidebar = () => {
    const reseller = offer ? `${offer.agentName ?? 'Your Reseller'}` : 'Your Reseller';
    const initials = reseller.split(' ').slice(0, 2).map((w: string) => w[0]).join('').toUpperCase();
    const faqs = [
      { q: 'How long until activation?', a: 'After accepting, our team will contact you within 2 business days to schedule installation. Most services are active within 5–10 business days.' },
      { q: 'Can I change my plan later?', a: 'Yes. You can upgrade at any time by contacting your reseller. Upgrades take effect from the next billing cycle. Downgrades are subject to the minimum contract period.' },
      { q: 'How is billing handled?', a: 'Monthly invoices are issued on your billing date and sent to your registered email. Payment via direct debit, Mada, or bank transfer. VAT invoices provided for all charges.' },
    ];
    return (
      <div>
        <div className="support-card support-sticky" style={{ position: 'sticky', top: 72 }}>
          <div className="support-card-head">
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--p)', marginBottom: 2 }}>Your Salam Reseller</div>
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>Questions about this offer?</div>
          </div>
          <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--p-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, color: 'var(--p)', flexShrink: 0 }}>{initials || 'SR'}</div>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{reseller}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Salam Authorized Reseller</div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <a href="tel:920000123" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--p)', textDecoration: 'none' }}>
                <Phone size={14} /> 920 000 123
              </a>
              <a href="mailto:support@salam.sa" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--p)', textDecoration: 'none' }}>
                <Mail size={14} /> support@salam.sa
              </a>
            </div>
          </div>
          <div style={{ paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '0 18px 8px' }}>Frequently Asked Questions</div>
            {faqs.map((faq, i) => (
              <div key={i} className="faq-item">
                <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <ChevronDown size={14} color="var(--text-3)" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }} />
                </div>
                <div className={`faq-answer${openFaq === i ? ' open' : ''}`}>{faq.a}</div>
              </div>
            ))}
          </div>
          <div style={{ padding: '14px 18px', borderTop: '1px solid var(--border)', background: 'var(--surface-alt)' }}>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>Salam Customer Care</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-1)' }}>920 000 123</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Sun–Thu 8am–10pm</div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Stage: Loading ──────────────────────────────────────────────────────────

  if (stage === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
          <Loader2 size={32} color="var(--p)" style={{ animation: 'spin .8s linear infinite' }} />
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Loading your offer…</p>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Expired ──────────────────────────────────────────────────────────

  if (stage === 'expired') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: '28px 16px' }}>
          <EdgeCard
            iconBg="var(--warn-bg)"
            icon={<Clock size={36} color="var(--warn)" />}
            title="This offer has expired"
            body={
              <>
                <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.65, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>
                  The link for this offer expired. Offer links are valid for 7 days from the date of issue.
                </p>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, textAlign: 'left', marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 10 }}>What can you do?</div>
                  {['Contact your reseller to request a new offer link', 'The reseller can re-send the same offer with a refreshed 7-day link'].map((s, i) => (
                    <div key={i} style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', gap: 8, marginBottom: 8 }}>
                      <ArrowRight size={14} color="var(--p)" style={{ flexShrink: 0, marginTop: 1 }} />{s}
                    </div>
                  ))}
                </div>
              </>
            }
            footer={
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="tel:920000123" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--p)', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13.5, fontWeight: 600 }}>
                  <Phone size={14} /> 920 000 123
                </a>
                <a href="mailto:support@salam.sa" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--surface)', color: 'var(--p)', border: '1.5px solid var(--p)', borderRadius: 8, textDecoration: 'none', fontSize: 13.5, fontWeight: 600 }}>
                  <Mail size={14} /> Email Support
                </a>
              </div>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Already Actioned ─────────────────────────────────────────────────

  if (stage === 'already-actioned') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: '28px 16px' }}>
          <EdgeCard
            iconBg="var(--succ-bg)"
            icon={<CheckCircle2 size={36} color="var(--succ)" />}
            title="Offer already actioned"
            body={
              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.65, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>
                This offer has already been accepted or is no longer available for review. No further action is required.
              </p>
            }
            footer={
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                Questions? Contact Salam Care: <strong>920 000 123</strong>
              </div>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Invalid ───────────────────────────────────────────────────────────

  if (stage === 'invalid') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: '28px 16px' }}>
          <EdgeCard
            iconBg="var(--err-bg)"
            icon={<Link2Off size={36} color="var(--err)" />}
            title="Invalid offer link"
            body={
              <>
                <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.65, marginBottom: 24, maxWidth: 360, margin: '0 auto 24px' }}>
                  {errorMsg || "This link doesn't match any offer in our system. It may have been entered incorrectly or the link may be corrupted."}
                </p>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 20px', marginBottom: 24, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
                  Please check the link in your email or SMS. If the problem persists, contact your reseller or Salam support.
                </div>
              </>
            }
            footer={
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href="tel:920000123" style={{ fontSize: 13.5, color: 'var(--p)', fontWeight: 500 }}>Salam Care: 920 000 123</a>
                <span style={{ color: 'var(--text-3)' }}>·</span>
                <a href="mailto:support@salam.sa" style={{ fontSize: 13.5, color: 'var(--p)', fontWeight: 500 }}>support@salam.sa</a>
              </div>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: OTP ───────────────────────────────────────────────────────────────

  if (stage === 'otp') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', padding: '28px 16px' }}>
          <div className="co-otp-wrap" style={{ width: '100%' }}>
            <div style={{ width: 60, height: 60, background: 'var(--p-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <ShieldCheck size={26} color="var(--p)" />
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: 'var(--text-1)' }}>
              {otpAction === 'accept' ? 'Confirm your acceptance' : 'Confirm your decision'}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, marginBottom: 6 }}>
              We've sent a 6-digit code to
            </p>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', marginBottom: 24 }}>
              {maskedContact || 'your registered number'}
            </div>

            {/* OTP inputs */}
            <div className="otp-group" style={{ marginBottom: 10 }}>
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => { otpRefs.current[i] = el; }}
                  className={`otp-inp${digit ? ' filled' : ''}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>

            {otpError && (
              <div style={{ color: 'var(--err)', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                <AlertCircle size={13} />{otpError}
              </div>
            )}

            {/* Resend */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              {resendCountdown > 0 ? (
                <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                  Resend code in <span style={{ fontWeight: 600 }}>{resendCountdown}s</span>
                </span>
              ) : (
                <button onClick={resendOtp} disabled={otpSending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit' }}>
                  {otpSending ? 'Sending…' : 'Resend code'}
                </button>
              )}
            </div>

            <button
              onClick={() => verifyOtp()}
              disabled={otpVerifying || otpDigits.join('').length < 6}
              style={{ width: '100%', padding: 13, background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: otpVerifying ? 'wait' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, opacity: otpDigits.join('').length < 6 ? 0.6 : 1, transition: 'all .15s' }}
            >
              {otpVerifying
                ? <><Loader2 size={16} style={{ animation: 'spin .8s linear infinite' }} /> Verifying…</>
                : <><CheckCircle size={16} /> Verify &amp; {otpAction === 'accept' ? 'Accept' : 'Decline'}</>}
            </button>

            <button onClick={() => setStage('landing')} style={{ width: '100%', marginTop: 8, padding: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-2)', fontSize: 13.5, fontFamily: 'inherit' }}>
              ← Back to Offer
            </button>

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>
              Your {otpAction === 'accept' ? 'acceptance' : 'decision'} is legally binding under Saudi electronic transactions law.
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Accepted ─────────────────────────────────────────────────────────

  if (stage === 'accepted') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: '28px 16px' }}>
          <div className="co-confirm-wrap" style={{ width: '100%' }}>
            <div className="co-confirm-head">
              <div style={{ width: 72, height: 72, background: 'var(--p-light)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CheckCircle2 size={34} color="var(--p)" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)', marginBottom: 8 }}>Thank you — offer confirmed!</h2>
              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto' }}>
                Your offer has been accepted and submitted to Salam Telecom for processing.
              </p>
              {offer && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, background: 'var(--surface-alt)', borderRadius: 8, padding: '8px 16px' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Offer Reference</span>
                  <span style={{ fontWeight: 700, color: 'var(--p)' }}>{offer.offerNumber}</span>
                </div>
              )}
            </div>
            <div className="co-confirm-body">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 16 }}>What happens next?</div>
              <div className="timeline">
                {[
                  { icon: <Check size={13} color="var(--p)" />, bg: 'var(--p-light)', title: 'Offer Accepted', sub: 'Today · Your acceptance has been recorded' },
                  { icon: <PhoneCall size={13} color="#3B82F6" />, bg: '#EFF6FF', title: 'Provisioning Contact', sub: 'Within 2 business days · Our team will contact you to schedule installation' },
                  { icon: <HardHat size={13} color="var(--warn)" />, bg: 'var(--warn-bg)', title: 'Installation & Testing', sub: '5–10 business days · Equipment installation and service testing' },
                  { icon: <Wifi size={13} color="var(--p)" />, bg: 'var(--p-light)', title: 'Service Live', sub: 'Your connectivity services will be fully active and billed from this date' },
                ].map((step, i, arr) => (
                  <div key={i} className="timeline-item" style={{ paddingBottom: i === arr.length - 1 ? 0 : 20 }}>
                    <div className="timeline-dot" style={{ background: step.bg }}>{step.icon}</div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>{step.title}</div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>{step.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="co-confirm-footer">
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Need help?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Phone size={14} color="var(--text-3)" /> Salam Care: <strong>920 000 123</strong>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Mail size={14} color="var(--text-3)" /> <strong>support@salam.sa</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Rejected ─────────────────────────────────────────────────────────

  if (stage === 'rejected') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: '28px 16px' }}>
          <div className="co-confirm-wrap" style={{ width: '100%' }}>
            <div className="co-confirm-head">
              <div style={{ width: 72, height: 72, background: 'var(--err-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <XCircle size={34} color="var(--err)" />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-1)', marginBottom: 8 }}>Offer declined</h2>
              <p style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 380, margin: '0 auto' }}>
                You've declined this offer. Your reseller will be notified.
              </p>
            </div>
            <div className="co-confirm-body">
              {!rejectionSubmitted ? (
                <>
                  <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 12 }}>
                    Would you mind sharing why? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span>
                  </div>
                  <select className="inp" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} style={{ marginBottom: 10 }}>
                    <option value="">Select a reason…</option>
                    <option value="expensive">Too expensive for our budget</option>
                    <option value="wrong-plan">Not the right plan for our needs</option>
                    <option value="elsewhere">Decided to go with another provider</option>
                    <option value="timing">Not the right timing</option>
                    <option value="other">Other reason</option>
                  </select>
                  {rejectReason === 'other' && (
                    <textarea className="inp" rows={3} placeholder="Tell us more…" value={rejectOther} onChange={(e) => setRejectOther(e.target.value)} style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 10 }} />
                  )}
                  <button onClick={() => setRejectionSubmitted(true)} style={{ width: '100%', padding: 13, background: 'var(--p)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Submit Feedback
                  </button>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px 0', color: 'var(--succ)', fontWeight: 600 }}>
                  <CheckCircle2 size={24} style={{ display: 'block', margin: '0 auto 8px' }} />
                  Thank you for your feedback.
                </div>
              )}
            </div>
            <div className="co-confirm-footer">
              <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.6 }}>
                Changed your mind? Contact your reseller to request a new offer.
              </div>
              <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="tel:920000123" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--p)', textDecoration: 'none' }}>
                  <Phone size={13} /> 920 000 123
                </a>
                <a href="mailto:support@salam.sa" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--p)', textDecoration: 'none' }}>
                  <Mail size={13} /> support@salam.sa
                </a>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Stage: Landing (main offer view) ────────────────────────────────────────

  if (!offer) return null;

  const items = offer.items ?? [];
  const subtotal = Number(offer.subtotal) || 0;
  const vatAmount = Number(offer.vatAmount) || 0;
  const total = Number(offer.total) || 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-alt)', display: 'flex', flexDirection: 'column' }}>
      <Header />

      <div className="co-body">
        {/* ── Left: Main content ── */}
        <div>
          <div className="offer-card">

            {/* Offer header */}
            <div className="offer-card-head">
              <div className="offer-card-head-grid" />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>
                  Connectivity Offer
                </div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4, lineHeight: 1.2 }}>
                  {offer.customer?.nameEn ? `Offer for ${offer.customer.nameEn}` : 'Commercial Offer'}
                </h1>
                {offer.customer?.nameAr && (
                  <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.7)', marginBottom: 12 }} dir="rtl">
                    {offer.customer.nameAr}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ background: 'rgba(255,255,255,.15)', borderRadius: 8, padding: '6px 12px', fontSize: 12.5, color: 'rgba(255,255,255,.9)', fontWeight: 600 }}>
                    Ref: {offer.offerNumber}
                  </div>
                  {offer.customerLinkExpiresAt && (
                    <div className="expiry-timer">
                      <Clock size={13} />
                      <span>
                        {(() => {
                          const days = Math.ceil((new Date(offer.customerLinkExpiresAt).getTime() - Date.now()) / 86400000);
                          return days > 0 ? `Expires in ${days} day${days !== 1 ? 's' : ''}` : 'Expiring soon';
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Services */}
            {items.length > 0 && (
              <div className="offer-section">
                <SectionTitle icon={<Package size={14} />}>Services Included</SectionTitle>
                {items.map((item) => {
                  const cat = item.product?.category ?? '';
                  const cfg = catConfig[cat] ?? { bg: '#F3F4F6', color: '#374151', Icon: Package };
                  const CatIcon = cfg.Icon;
                  return (
                    <div key={item.id} className="service-line">
                      <div className="service-icon" style={{ background: cfg.bg }}>
                        <CatIcon size={18} color={cfg.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ marginBottom: 3 }}>
                          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }}>
                            {catLabels[cat] ?? cat}
                          </span>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)', marginBottom: 2 }}>
                          {item.product?.name ?? 'Service'}
                        </div>
                        {item.location && (
                          <div style={{ fontSize: 12, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <MapPin size={11} />
                            {item.location.addressLine1
                              ? `${item.location.addressLine1}${item.location.city ? `, ${item.location.city}` : ''}`
                              : `${Number(item.location.lat).toFixed(4)}, ${Number(item.location.lng).toFixed(4)}`}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 17, color: 'var(--text-1)' }}>
                          {formatSAR(item.price)}
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>/month</div>
                        <div style={{ fontSize: 11, color: 'var(--p)', fontWeight: 600, marginTop: 4 }}>
                          Monthly billing
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                          {((item as any).contractMonths ?? 12)}-month contract
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pricing */}
            <div className="offer-section">
              <SectionTitle icon={<Receipt size={14} />}>Pricing Summary</SectionTitle>
              {items.map((item) => (
                <div key={item.id} className="price-row">
                  <span style={{ color: 'var(--text-2)' }}>{item.product?.name ?? 'Service'}</span>
                  <span style={{ fontWeight: 500 }}>{formatSAR(item.price)}</span>
                </div>
              ))}
              <div className="price-row">
                <span style={{ color: 'var(--text-2)' }}>Subtotal (excl. VAT)</span>
                <span style={{ fontWeight: 600 }}>{formatSAR(subtotal)}</span>
              </div>
              <div className="price-row">
                <span style={{ color: 'var(--text-2)' }}>VAT (15%)</span>
                <span style={{ fontWeight: 600 }}>{formatSAR(vatAmount)}</span>
              </div>
              <div className="price-total">
                <span style={{ fontWeight: 700, fontSize: 16 }}>Total Monthly Payment</span>
                <span style={{ fontWeight: 800, fontSize: 22, color: 'var(--p)' }}>{formatSAR(total)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'right', marginTop: -4 }}>
                Inclusive of VAT · Monthly recurring charge
              </div>
            </div>

            {/* Company info */}
            {offer.customer && (
              <div className="offer-section">
                <SectionTitle icon={<Building2 size={14} />}>Company Information</SectionTitle>
                <InfoGrid>
                  {offer.customer.nameEn && <InfoCell label="Company (EN)" value={offer.customer.nameEn} />}
                  {offer.customer.nameAr && <InfoCell label="Company (AR)" value={offer.customer.nameAr} rtl />}
                  {offer.customer.crNumber && <InfoCell label="CR Number" value={offer.customer.crNumber} />}
                  {offer.customer.vatNumber && <InfoCell label="VAT Number" value={offer.customer.vatNumber} />}
                  {offer.customer.contactEmail && <InfoCell label="Email" value={offer.customer.contactEmail} />}
                  {offer.customer.contactMobile && <InfoCell label="Mobile" value={offer.customer.contactMobile} />}
                </InfoGrid>
              </div>
            )}

            {/* Authorized Person */}
            {offer.authorizedPerson && (
              <div className="offer-section">
                <SectionTitle icon={<UserCheck size={14} />}>Authorised Signatory</SectionTitle>
                <InfoGrid>
                  {offer.authorizedPerson.nameEn && <InfoCell label="Full Name" value={offer.authorizedPerson.nameEn} />}
                  {offer.authorizedPerson.mobile && <InfoCell label="Mobile" value={offer.authorizedPerson.mobile} />}
                  {offer.authorizedPerson.idType && <InfoCell label="ID Type" value={offer.authorizedPerson.idType} />}
                </InfoGrid>
                <div style={{ background: 'var(--p-pale)', border: '1px solid var(--p-light)', borderRadius: 8, padding: '10px 14px', marginTop: 12, fontSize: 12.5, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Info size={14} color="var(--p)" style={{ flexShrink: 0 }} />
                  You will be asked to verify your identity with a one-time SMS code before accepting this offer.
                </div>
              </div>
            )}

            {/* T&Cs */}
            <div className="offer-section">
              <SectionTitle icon={<FileText size={14} />}>Terms &amp; Conditions</SectionTitle>
              <div className="tcs-toggle" onClick={() => setTcsOpen(!tcsOpen)}>
                <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-1)' }}>View full Terms &amp; Conditions</span>
                <ChevronDown size={16} color="var(--text-3)" style={{ transform: tcsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </div>
              <div className={`tcs-content${tcsOpen ? ' open' : ''}`}>
                <p style={{ marginBottom: 10 }}><strong>1. Service Agreement.</strong> By accepting this offer, the Customer agrees to subscribe to the Salam Telecom connectivity services described herein for the minimum contract period stated for each service.</p>
                <p style={{ marginBottom: 10 }}><strong>2. Payment Terms.</strong> Monthly charges are due in advance on the billing date. Payments may be made via direct debit, credit card, or bank transfer. Late payments incur a 2% monthly surcharge.</p>
                <p style={{ marginBottom: 10 }}><strong>3. Early Termination.</strong> Cancelling before the minimum contract period results in an early termination fee equal to the remaining monthly charges for that service.</p>
                <p style={{ marginBottom: 10 }}><strong>4. Service Levels.</strong> Salam Telecom will use reasonable endeavours to maintain the uptime commitments specified for each product. Service credits apply as per the applicable SLA.</p>
                <p style={{ marginBottom: 10 }}><strong>5. Acceptance.</strong> Electronic acceptance via OTP constitutes a legally binding agreement under Saudi electronic transactions law.</p>
                <p><strong>6. Governing Law.</strong> This agreement is governed by the laws of the Kingdom of Saudi Arabia. Disputes are subject to the jurisdiction of Saudi commercial courts.</p>
              </div>
              {/* T&C acceptance checkbox */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--p)', marginTop: 2, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 }}>
                  I have read and agree to the <strong style={{ color: 'var(--text-1)' }}>Terms &amp; Conditions</strong> above, and confirm that I am authorised to accept this offer on behalf of my organisation.
                </span>
              </label>
            </div>

            {/* CTA Bar */}
            <div className="co-cta-bar">
              <button
                onClick={() => sendOtp('reject')}
                disabled={otpSending}
                style={{ flex: 1, padding: 13, background: 'transparent', color: 'var(--p)', border: '1.5px solid var(--p)', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'all .15s' }}
              >
                {otpSending && otpAction === 'reject' ? <Loader2 size={16} style={{ animation: 'spin .8s linear infinite' }} /> : <XCircle size={16} />}
                Reject Offer
              </button>
              <button
                onClick={() => sendOtp('accept')}
                disabled={otpSending || !termsAccepted}
                title={!termsAccepted ? 'Please accept the Terms & Conditions first' : undefined}
                style={{ flex: 2, padding: 13, background: termsAccepted ? 'var(--p)' : 'var(--border)', color: termsAccepted ? '#fff' : 'var(--text-3)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: termsAccepted ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, boxShadow: termsAccepted ? '0 4px 16px rgba(0,56,49,.3)' : 'none', transition: 'all .15s' }}
              >
                {otpSending && otpAction === 'accept' ? <Loader2 size={16} style={{ animation: 'spin .8s linear infinite' }} /> : <CheckCircle2 size={16} />}
                Accept Offer
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Support sidebar ── */}
        <SupportSidebar />
      </div>

      <Footer />
    </div>
  );
};
